<script setup lang="ts">
import { formatDate, formatMoney } from '@taler/domain'
import { StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type Snapshot, useAccountReconciliation } from '../composables/use-account-reconciliation.ts'
import type { AccountRow } from '../internal/account-tree.ts'
import { snapshotOutcome } from '../internal/reconciliation.ts'

const props = defineProps<{
  ledgerId: string
  account: AccountRow
  canReconcile: boolean
  openReconciliationId: string | undefined
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const reconciliation = useAccountReconciliation({
  ledgerId: () => props.ledgerId,
  accountId: () => props.account.id,
  enabled: () => true,
})

function levelText(value: Snapshot['observedBalance']): string {
  return formatMoney(value, { locale: currentLocale.value })
}

function dateLabel(snapshot: Snapshot): string {
  return formatDate(snapshot.observedDate, currentLocale.value)
}
</script>

<template>
  <section
    class="flex flex-col gap-[var(--space-inline)] border-t border-[color:var(--color-border)] pt-[var(--space-cell)]"
  >
    <div class="flex items-center justify-between gap-[var(--space-cell)]">
      <h4 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ t('accounts.reconcileHistoryTitle') }}
      </h4>
      <RouterLink
        v-if="canReconcile"
        :to="
          openReconciliationId !== undefined
            ? { name: 'reconciliation', params: { id: openReconciliationId } }
            : { name: 'reconciliations' }
        "
        class="text-[length:var(--text-step-2)] text-[color:var(--color-accent)] underline"
      >
        {{ t('accounts.reconcileAccounts') }}
      </RouterLink>
    </div>

    <p
      v-if="reconciliation.isLoadingHistory.value"
      role="status"
      class="text-[color:var(--color-text-muted)]"
    >
      {{ t('accounts.reconcileLoadingHistory') }}
    </p>
    <p v-else-if="reconciliation.historyError.value" class="text-[color:var(--color-danger)]">
      {{ t('accounts.reconcileHistoryError') }}
    </p>
    <p v-else-if="reconciliation.snapshots.value.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('accounts.reconcileHistoryEmpty') }}
    </p>
    <ul v-else class="flex flex-col divide-y divide-[color:var(--color-border)]">
      <li
        v-for="snapshot in reconciliation.snapshots.value"
        :key="snapshot.id"
        class="flex flex-col gap-[var(--space-hair)] py-[var(--space-inline)] text-[length:var(--text-step-2)]"
      >
        <div class="flex items-center justify-between gap-[var(--space-cell)]">
          <span class="text-[color:var(--color-text)]">{{ dateLabel(snapshot) }}</span>
          <span class="tabular-nums text-[color:var(--color-text)]">{{
            levelText(snapshot.observedBalance)
          }}</span>
        </div>
        <div class="flex items-center gap-[var(--space-inline)]">
          <StatusTag v-if="snapshotOutcome(snapshot) === 'matched'" tone="ok">
            {{ t('accounts.reconcileOutcomeMatched') }}
          </StatusTag>
          <StatusTag v-else-if="snapshotOutcome(snapshot) === 'reversed'" tone="danger">
            {{ t('accounts.reconcileOutcomeReversed') }}
          </StatusTag>
          <span
            v-else
            class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-attention)]"
          >
            {{ t('accounts.reconcileOutcomeCorrected') }}
          </span>
          <RouterLink
            v-if="snapshot.correctingEntryId !== undefined"
            :to="{ name: 'transactions', query: { entry: snapshot.correctingEntryId } }"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)] underline"
          >
            {{ t('accounts.reconcileViewEntry') }}
          </RouterLink>
          <RouterLink
            v-if="snapshot.reconciliationId !== undefined"
            :to="{ name: 'reconciliation', params: { id: snapshot.reconciliationId } }"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)] underline"
          >
            {{ t('accounts.reconcileViewSession') }}
          </RouterLink>
        </div>
      </li>
    </ul>
  </section>
</template>
